import { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { isNil, noop, toString } from 'lodash';
import { useTranslation } from 'react-i18next';
// Import Hooks
import { useGTMEvent } from 'hooks/UseGTM';
import useHasPermission from 'hooks/UseHasPermission';

// Import UI Components
import { Button, Tooltip } from '@geneui/components';
import AccessControl from 'components/common/AccessControl';
import ActionLink from 'components/common/ActionLink';
// Import Constants
import {
    CustomerJourneyStatusLabels,
    l,
    ActionsIcons,
    RealtimePromotionTypes,
    GACategoryEvents,
} from 'constants/common';
// Import Version History
import { useVersionHistoryContext, VersionHistoryIconWithPopover } from 'components/VersionHistory';
// Import Enums
import { PermissionEnum, ObjectTypeEnum, SubscriptionSectionEnum } from 'enums';

const { GADetailsCategory } = GACategoryEvents;

const CustomerJourneyActionsButtons = ({
    data,
    unArchiveCustomerJourneyAction,
    archiveCustomerJourneyAction,
    deleteCustomerJourneyAction,
    getCloneCustomerJourneyPath,
    editCustomerJourneyAction,
    getEditCustomerJourneyPath,
    stopSendCustomerJourneyAction,
    startCustomerJourneyAction,
    getViewCustomerJourneyPath,
    resumeCustomerJourneyAction,
    infoCustomerJourneyAction,
    getReportCustomerJourneyPath,
    getCreateCustomerJourneyVisualizationPath,
    getEditCustomerJourneyVisualizationPath,
    activityTimelineAction,
    getClonePredefinedPath,
    isInfoVisible,
    handleCloseModal,
}) => {
    const { t } = useTranslation();
    const { hasPermission, hasSubscriptionSection } = useHasPermission();
    const { logEvent } = useGTMEvent();
    const hasExecutePermission = useMemo(() => hasPermission(PermissionEnum.ExecuteWorkflow), [hasPermission]);

    const isPredefinedAvailable = hasSubscriptionSection(SubscriptionSectionEnum.Predefined);

    const { pointInTime } = useVersionHistoryContext();

    const { WorkflowId, Status, HasVisualization, isViewPage, IsUsed, ArchivedDate, PromotionType } = data;
    const status = toString(Status);

    const isCompleted = status === CustomerJourneyStatusLabels.Completed;
    const isInProgress = status === CustomerJourneyStatusLabels.InProgress;
    const isFailed = status === CustomerJourneyStatusLabels.Failed;
    const isInvalid = status === CustomerJourneyStatusLabels.Invalid;
    const isSuspended = status === CustomerJourneyStatusLabels.Suspended;
    const isNonStarted = status === CustomerJourneyStatusLabels.NonStarted;
    const isVersionViewMode = !isNil(pointInTime);

    const isStarting = status === CustomerJourneyStatusLabels.Starting;
    const isUpdating = status === CustomerJourneyStatusLabels.Updating;
    const isResuming = status === CustomerJourneyStatusLabels.Resuming;
    const isSuspending = status === CustomerJourneyStatusLabels.Suspending;

    const isVisibleActionButtons = !isUpdating && !isStarting && !isResuming && !isSuspending;

    const isArchived = !isNil(ArchivedDate);
    const isCJ = PromotionType === +RealtimePromotionTypes.CustomerJourney;

    const isVisibleStopSend = () =>
        isInProgress && !isVersionViewMode && isVisibleActionButtons && hasExecutePermission;
    const isVisibleStart = () =>
        !isCompleted &&
        !isFailed &&
        !isInProgress &&
        !isVersionViewMode &&
        !isInvalid &&
        !isArchived &&
        !isSuspended &&
        isVisibleActionButtons &&
        hasExecutePermission;
    const isVisibleDelete = useMemo(
        () =>
            !isInProgress &&
            !isCompleted &&
            !isVersionViewMode &&
            !isSuspended &&
            !isFailed &&
            !IsUsed &&
            isVisibleActionButtons &&
            hasPermission(PermissionEnum.DeleteWorkflow),
        [
            IsUsed,
            hasPermission,
            isCompleted,
            isFailed,
            isInProgress,
            isSuspended,
            isVersionViewMode,
            isVisibleActionButtons,
        ],
    );
    const isVisibleArchive = () =>
        !isVisibleDelete && !isVersionViewMode && !isInProgress && !isArchived && isVisibleActionButtons;
    const isVisibleUnArchive = () => isArchived && !isVersionViewMode && isVisibleActionButtons;
    const isVisibleView = () => !isInvalid && !isViewPage && isVisibleActionButtons;
    const isVisibleResume = () => isSuspended && !isVersionViewMode && isVisibleActionButtons && hasExecutePermission;
    const isVisibleInfo = () => !isViewPage && isInfoVisible;
    const isVisibleActivityTimeline = () => isViewPage && isVisibleActionButtons;
    const isVisibleReport = () => !isNonStarted && !isVersionViewMode && !isInvalid && isVisibleActionButtons;
    const isVisibleEdit = useMemo(() => {
        if (isVersionViewMode) {
            return false;
        }
        if (!isVisibleActionButtons) {
            return false;
        }

        if (isInProgress || isSuspended) {
            return hasPermission([PermissionEnum.ExecuteWorkflow, PermissionEnum.EditWorkflow], true);
        } else {
            return hasPermission(PermissionEnum.EditWorkflow);
        }
    }, [hasPermission, isInProgress, isSuspended, isVisibleActionButtons, isVersionViewMode]);
    const isVisibleVersionHistory = isViewPage;

    const isVisibleClone = useMemo(
        () =>
            !isVersionViewMode &&
            isVisibleActionButtons &&
            hasPermission([PermissionEnum.CreateWorkflow, PermissionEnum.ViewWorkflow], true),
        [hasPermission, isVisibleActionButtons, isVersionViewMode],
    );

    const onReportAction = () => {
        logEvent(
            GADetailsCategory.name,
            GADetailsCategory.events.journeyReportActionButton,
            GADetailsCategory.labels.journeyReportActionButton,
            1,
        );
        handleCloseModal();
    };

    const isVisibleCreateCustomerJourneyVisualization = () =>
        !HasVisualization && !isVersionViewMode && !isInvalid && isCJ && isVisibleActionButtons;
    const isVisibleEditCustomerJourneyVisualization = () =>
        HasVisualization && !isInvalid && isCJ && isVisibleActionButtons;

    return (
        <>
            {isVisibleVersionHistory && (
                <VersionHistoryIconWithPopover objectId={WorkflowId} objectTypeId={ObjectTypeEnum.Workflow} />
            )}
            {isVisibleDelete && (
                <Tooltip text={t(isCJ ? l.DeleteCustomerJourney : l.DeleteActionCampaign)}>
                    <Button
                        icon={ActionsIcons.Delete}
                        appearance="minimal"
                        color="primary"
                        onClick={() => deleteCustomerJourneyAction(data)}
                    />
                </Tooltip>
            )}
            {isVisibleEdit && (
                <Tooltip text={t(isCJ ? l.EditCustomerJourney : l.EditActionCampaign)}>
                    <ActionLink
                        icon={ActionsIcons.Edit}
                        appearance="minimal"
                        color="primary"
                        withConfirm={IsUsed}
                        onClick={() => editCustomerJourneyAction(data)}
                        redirectPath={getEditCustomerJourneyPath(data)}
                        onRedirect={handleCloseModal}
                    />
                </Tooltip>
            )}
            {isVisibleEditCustomerJourneyVisualization() && (
                <AccessControl permissions={PermissionEnum.ManageWorkflow}>
                    <Tooltip text={t(l.EditCustomerJourneyVisualization)}>
                        <ActionLink
                            icon={ActionsIcons.Visualization}
                            appearance="minimal"
                            color="primary"
                            redirectPath={getEditCustomerJourneyVisualizationPath(data)}
                            onRedirect={handleCloseModal}
                        />
                    </Tooltip>
                </AccessControl>
            )}
            {isVisibleArchive() && (
                <AccessControl permissions={PermissionEnum.ManageWorkflow}>
                    <Tooltip text={t(isCJ ? l.ArchiveCustomerJourney : l.ArchiveActionCampaign)}>
                        <Button
                            icon={ActionsIcons.Archive}
                            appearance="minimal"
                            color="primary"
                            onClick={() => archiveCustomerJourneyAction(data)}
                        />
                    </Tooltip>
                </AccessControl>
            )}
            {isVisibleUnArchive() && (
                <AccessControl permissions={PermissionEnum.ManageWorkflow}>
                    <Tooltip text={t(isCJ ? l.UnarchiveCustomerJourney : l.UnarchiveActionCampaign)}>
                        <Button
                            icon={ActionsIcons.UnArchive}
                            appearance="minimal"
                            color="primary"
                            onClick={() => unArchiveCustomerJourneyAction(data)}
                        />
                    </Tooltip>
                </AccessControl>
            )}
            {isVisibleView() && (
                <AccessControl permissions={PermissionEnum.ManageWorkflow}>
                    <Tooltip text={t(isCJ ? l.ViewCustomerJourney : l.ViewActionCampaign)}>
                        <ActionLink
                            icon={ActionsIcons.ViewDoc}
                            appearance="minimal"
                            color="primary"
                            redirectPath={getViewCustomerJourneyPath(data)}
                            onRedirect={handleCloseModal}
                        />
                    </Tooltip>
                </AccessControl>
            )}
            {isVisibleCreateCustomerJourneyVisualization() && (
                <AccessControl permissions={PermissionEnum.ManageWorkflow}>
                    <Tooltip text={t(l.CreateCustomerJourneyVisualization)}>
                        <ActionLink
                            icon={ActionsIcons.Visualization}
                            appearance="minimal"
                            color="primary"
                            redirectPath={getCreateCustomerJourneyVisualizationPath(data)}
                            onRedirect={handleCloseModal}
                        />
                    </Tooltip>
                </AccessControl>
            )}
            {isVisibleReport() && (
                <AccessControl permissions={PermissionEnum.ManageWorkflow}>
                    <Tooltip text={t(isCJ ? l.CustomerJourneyReport : l.ReportActionCampaign)}>
                        <ActionLink
                            icon={ActionsIcons.ViewReport}
                            appearance="minimal"
                            color="primary"
                            redirectPath={getReportCustomerJourneyPath(data)}
                            onRedirect={onReportAction}
                        />
                    </Tooltip>
                </AccessControl>
            )}
            {isVisibleStart() && (
                <Tooltip text={t(isCJ ? l.StartCustomerJourney : l.StartActionCampaign)}>
                    <Button
                        icon={ActionsIcons.Start}
                        appearance="minimal"
                        color="primary"
                        onClick={() => startCustomerJourneyAction(data)}
                    />
                </Tooltip>
            )}
            {isVisibleStopSend() && (
                <Tooltip text={t(isCJ ? l.StopCustomerJourney : l.StopActionCampaign)}>
                    <Button
                        icon={ActionsIcons.StatusStop}
                        appearance="minimal"
                        color="primary"
                        onClick={() => stopSendCustomerJourneyAction(data)}
                    />
                </Tooltip>
            )}
            {isVisibleResume() && (
                <Tooltip text={t(isCJ ? l.ResumeCustomerJourney : l.ResumeActionCampaign)}>
                    <Button
                        icon={ActionsIcons.Resume}
                        appearance="minimal"
                        color="primary"
                        onClick={() => resumeCustomerJourneyAction(data)}
                    />
                </Tooltip>
            )}
            {isVisibleClone && (
                <Tooltip text={t(isCJ ? l.CloneCustomerJourney : l.CloneActionCampaign)}>
                    <ActionLink
                        icon={ActionsIcons.Clone}
                        appearance="minimal"
                        color="primary"
                        redirectPath={getCloneCustomerJourneyPath(data)}
                        onRedirect={handleCloseModal}
                    />
                </Tooltip>
            )}
            {isVisibleClone && isPredefinedAvailable && getClonePredefinedPath && (
                <AccessControl permissions={[PermissionEnum.CRMAdmin, PermissionEnum.CRMManager]}>
                    <Tooltip text={t(l.CloneAsPredefined)}>
                        <ActionLink
                            icon={ActionsIcons.Predefined}
                            appearance="minimal"
                            color="primary"
                            redirectPath={getClonePredefinedPath(data)}
                            onRedirect={handleCloseModal}
                        />
                    </Tooltip>
                </AccessControl>
            )}
            {isVisibleInfo() && (
                <Tooltip text={t(isCJ ? l.CJInfo : l.ActionCampaignInfo)}>
                    <Button
                        icon={ActionsIcons.Stop}
                        appearance="minimal"
                        color="primary"
                        onClick={() => infoCustomerJourneyAction(data)}
                    />
                </Tooltip>
            )}
            {isVisibleActivityTimeline() && (
                <Tooltip text={t(l.ActivityTimeline)}>
                    <Button
                        icon="bc-icon-detailed-report"
                        appearance="minimal"
                        color="primary"
                        onClick={() => activityTimelineAction(data)}
                    />
                </Tooltip>
            )}
        </>
    );
};

CustomerJourneyActionsButtons.propTypes = {
    data: PropTypes.object.isRequired,
    unArchiveCustomerJourneyAction: PropTypes.func,
    archiveCustomerJourneyAction: PropTypes.func,
    deleteCustomerJourneyAction: PropTypes.func,
    getCloneCustomerJourneyPath: PropTypes.func,
    stopSendCustomerJourneyAction: PropTypes.func,
    startCustomerJourneyAction: PropTypes.func,
    editCustomerJourneyAction: PropTypes.func,
    getEditCustomerJourneyPath: PropTypes.func,
    getViewCustomerJourneyPath: PropTypes.func,
    resumeCustomerJourneyAction: PropTypes.func,
    infoCustomerJourneyAction: PropTypes.func,
    getReportCustomerJourneyPath: PropTypes.func,
    getCreateCustomerJourneyVisualizationPath: PropTypes.func,
    getEditCustomerJourneyVisualizationPath: PropTypes.func,
    getClonePredefinedPath: PropTypes.func,
    activityTimelineAction: PropTypes.func,
    isViewPage: PropTypes.bool,
    isInfoVisible: PropTypes.bool,
    handleCloseModal: PropTypes.func,
};

CustomerJourneyActionsButtons.defaultProps = {
    unArchiveCustomerJourneyAction: noop,
    archiveCustomerJourneyAction: noop,
    deleteCustomerJourneyAction: noop,
    getCloneCustomerJourneyPath: noop,
    editCustomerJourneyAction: noop,
    getEditCustomerJourneyPath: noop,
    stopSendCustomerJourneyAction: noop,
    startCustomerJourneyAction: noop,
    getViewCustomerJourneyPath: noop,
    resumeCustomerJourneyAction: noop,
    infoCustomerJourneyAction: noop,
    getReportCustomerJourneyPath: noop,
    getCreateCustomerJourneyVisualizationPath: noop,
    getEditCustomerJourneyVisualizationPath: noop,
    activityTimelineAction: noop,
    isViewPage: false,
    isInfoVisible: true,
    handleCloseModal: noop,
};

export default memo(CustomerJourneyActionsButtons);
